import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '../components/Icon';
import { faSliders, faXmark, faMagnifyingGlass, faHeadset } from '@fortawesome/free-solid-svg-icons';
import { useSettings } from '../lib/store';
import { useFetch } from '../lib/useFetch';
import { usageLabel } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { ProductGrid } from '../components/ProductCard';
import { Empty, PageHead } from '../components/ui';

const KEYS = ['category', 'type', 'usage', 'q', 'sort'];

function RadioGroup({ title, name, value, options, onChange }) {
  return (
    <fieldset className="card p-4">
      <legend className="sr-only">{title}</legend>
      <h2 className="mb-2.5 text-xs font-bold tracking-wider text-slate-500 uppercase">{title}</h2>
      <div className="flex flex-col gap-0.5">
        {options.map(([v, label]) => (
          <label key={v} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-semibold hover:bg-soft has-[:checked]:text-brand-600">
            <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(name, v)} className="accent-brand-500" />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function Shop() {
  const { settings: s } = useSettings();
  const { t, pick } = useI18n();
  const [params, setParams] = useSearchParams();
  const state = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || '']));
  const [q, setQ] = useState(state.q);
  const [showFilters, setShowFilters] = useState(false);

  const update = (key, value) => {
    const next = { ...state, [key]: value };
    if (key === 'category' && value === 'installation') next.type = '';
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v)), { replace: true });
  };

  // Debounced search box -> URL
  useEffect(() => {
    if (q.trim() === state.q) return undefined;
    const timer = setTimeout(() => update('q', q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);
  useEffect(() => setQ(state.q), [state.q]);

  const qs = new URLSearchParams(Object.entries(state).filter(([, v]) => v)).toString();
  const { data, loading } = useFetch(`/api/products${qs ? `?${qs}` : ''}`);
  const list = data || [];

  const cat = s.categories.find((c) => c.key === state.category);
  const baseTitle = cat ? pick(cat, 'name') : t('All Products');
  const title = `${state.type ? t('{type} {category}', { type: state.type, category: baseTitle }) : baseTitle}${state.usage ? ` – ${t(usageLabel(state.usage))}` : ''}`;
  useEffect(() => { document.title = `${baseTitle} – ${pick(s, 'storeName')}`; }, [baseTitle, s.storeName]);

  const chips = [
    state.type && ['type', t('Type: {type}', { type: state.type })],
    state.usage && ['usage', t(usageLabel(state.usage))],
    state.q && ['q', `“${state.q}”`],
  ].filter(Boolean);

  return (
    <>
      <PageHead title={title} crumbs={[[t('Home'), '/'], [baseTitle]]}>
        <p className="mt-1.5 text-slate-500">{cat ? pick(cat, 'blurb') : t('EV chargers, installation services, adapters and cables.')}</p>
      </PageHead>

      <div className="shell grid gap-7 pt-8 pb-16 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className={`${showFilters ? 'flex' : 'hidden'} flex-col gap-4 self-start lg:sticky lg:top-24 lg:flex`}>
          <RadioGroup title={t('Category')} name="category" value={state.category} onChange={update}
            options={[['', t('All products')], ...s.categories.map((c) => [c.key, pick(c, 'name')])]} />
          {state.category !== 'installation' && (
            <RadioGroup title={t('Charger type')} name="type" value={state.type} onChange={update}
              options={[['', t('All')], ['AC', t('AC (Level 2)')], ['DC', t('DC Fast')]]} />
          )}
          <RadioGroup title={t('Use')} name="usage" value={state.usage} onChange={update}
            options={[['', t('All')], ['private', t('Private / Home')], ['public', t('Public / Commercial')]]} />
          <Link to="/installation#quote" className="btn btn-outline w-full"><FontAwesomeIcon icon={faHeadset} /> {t('Need help choosing?')}</Link>
        </aside>

        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="relative min-w-52 flex-1 sm:max-w-sm">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute top-1/2 start-3.5 -translate-y-1/2 text-slate-400" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Search products…')} className="input ps-10" aria-label={t('Search products')} />
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-outline btn-sm lg:hidden" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}>
                <FontAwesomeIcon icon={faSliders} /> {t('Filters')}
              </button>
              <select value={state.sort} onChange={(e) => update('sort', e.target.value)} className="input w-auto" aria-label={t('Sort')}>
                <option value="">{t('Featured')}</option>
                <option value="price-asc">{t('Price: low to high')}</option>
                <option value="price-desc">{t('Price: high to low')}</option>
                <option value="discount">{t('Biggest discount')}</option>
                <option value="name">{t('Name A–Z')}</option>
              </select>
            </div>
          </div>

          {chips.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {chips.map(([k, label]) => (
                <button key={k} onClick={() => update(k, '')}
                  className="cursor-pointer rounded-full border border-slate-200 bg-soft px-3 py-1 text-[13px] font-semibold transition hover:border-sale hover:text-sale">
                  {label} <FontAwesomeIcon icon={faXmark} className="ms-1" />
                </button>
              ))}
            </div>
          )}
          <p className="mb-3.5 text-[13px] text-slate-500">{loading ? t('Loading…') : list.length === 1 ? t('1 product') : t('{n} products', { n: list.length })}</p>

          <ProductGrid products={list} loading={loading} cols={3} skeletons={6}
            empty={<Empty>{t('No products match these filters.')}<br /><Link to="/shop" className="btn btn-outline btn-sm mt-5">{t('Clear filters')}</Link></Empty>} />
        </div>
      </div>
    </>
  );
}
